import { useState } from "react";
import { CheckGrid, YesNo, FileInput, SubmitButton, SuccessBox, postMultipart, inputCls, labelCls, toast } from "@/components/formkit";

const INTERESTS = ["Independent Subcontractor", "Project-Based Technician", "W-2 Employment Opportunities", "Installation Crew / Company"];
const EXPERTISE = [
  "TV Mounting", "Home Theater", "Audio / Video", "Smart Home Installation", "Security Cameras / CCTV",
  "Structured Cabling", "Cat5e / Cat6 / Cat6A", "Fiber Optic Installation", "Fiber Splicing", "OTDR Testing",
  "Commercial Low Voltage", "Data Center Infrastructure", "Networking / Wi-Fi", "Access Control",
  "Computer Repair", "Electronics Installation", "Solar Installation", "Residential AI Automation",
  "Smart Home AI Integration", "Home Automation Platforms", "Local AI Deployment", "AI Workstation Assembly",
  "GPU Server Installation", "AI Infrastructure", "Business AI Automation", "Enterprise AI Infrastructure",
  "Server Administration", "Solar Panel Installer", "Solar Racking Technician", "Residential Solar Installer",
  "Commercial Solar Installer", "Solar Installation Crew", "Solar Maintenance Technician", "Other",
];
const CERTS = ["OSHA 10", "OSHA 30", "BICSI", "CompTIA", "Fiber Certifications", "Lift Certifications", "Other"];

const CONFIRM = `Thank you for your interest in DuroTech28. Your application has been received.

Our team will review your information and contact you if an opportunity matches your qualifications.`;

const EMPTY = {
  full_name: "", email: "", phone: "", city_state: "", years_experience: "",
  company_name: "", portfolio_url: "",
  employment_interest: [], expertise: [], certifications: [],
  transportation: "", tools: "", insurance: "", willing_travel: "", own_crew: "",
  service_areas: "", experience: "",
};

export default function TechnicianForm() {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState({ resume: null, certifications_file: null });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => (opt) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(opt) ? f[k].filter((x) => x !== opt) : [...f[k], opt] }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await postMultipart("/applications/technician", form, files);
      setDone(true);
      setForm(EMPTY);
      setFiles({ resume: null, certifications_file: null });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <SuccessBox message={CONFIRM} onReset={() => setDone(false)} testid="tech-success" />;

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="technician-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Full Name *</label>
          <input required value={form.full_name} onChange={set("full_name")} data-testid="tech-name" className={inputCls} /></div>
        <div><label className={labelCls}>Email Address *</label>
          <input required type="email" value={form.email} onChange={set("email")} data-testid="tech-email" className={inputCls} /></div>
        <div><label className={labelCls}>Phone Number *</label>
          <input required value={form.phone} onChange={set("phone")} data-testid="tech-phone" className={inputCls} /></div>
        <div><label className={labelCls}>City and State *</label>
          <input required value={form.city_state} onChange={set("city_state")} placeholder="Dallas, TX" data-testid="tech-city" className={inputCls} /></div>
        <div><label className={labelCls}>Years of Experience *</label>
          <input required value={form.years_experience} onChange={set("years_experience")} data-testid="tech-years" className={inputCls} /></div>
        <div><label className={labelCls}>Company Name (optional)</label>
          <input value={form.company_name} onChange={set("company_name")} data-testid="tech-company" className={inputCls} /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Website or Portfolio (optional)</label>
          <input value={form.portfolio_url} onChange={set("portfolio_url")} data-testid="tech-portfolio" className={inputCls} /></div>
      </div>

      <div>
        <span className={labelCls}>Employment Interest</span>
        <CheckGrid options={INTERESTS} values={form.employment_interest} onToggle={toggle("employment_interest")} testPrefix="tech-interest" />
      </div>
      <div>
        <span className={labelCls}>Areas of Expertise — select all that apply</span>
        <CheckGrid options={EXPERTISE} values={form.expertise} onToggle={toggle("expertise")} testPrefix="tech-expertise" />
      </div>
      <div>
        <span className={labelCls}>Certifications</span>
        <CheckGrid options={CERTS} values={form.certifications} onToggle={toggle("certifications")} testPrefix="tech-cert" />
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <YesNo label="Reliable transportation?" value={form.transportation} onChange={(v) => setForm((f) => ({ ...f, transportation: v }))} testPrefix="tech-transport" />
        <YesNo label="Own professional tools?" value={form.tools} onChange={(v) => setForm((f) => ({ ...f, tools: v }))} testPrefix="tech-tools" />
        <YesNo label="Carry liability insurance?" value={form.insurance} onChange={(v) => setForm((f) => ({ ...f, insurance: v }))} testPrefix="tech-insurance" />
        <YesNo label="Willing to travel?" value={form.willing_travel} onChange={(v) => setForm((f) => ({ ...f, willing_travel: v }))} testPrefix="tech-travel" />
        <YesNo label="Own installation crew?" value={form.own_crew} onChange={(v) => setForm((f) => ({ ...f, own_crew: v }))} testPrefix="tech-crew" />
      </div>

      <div><label className={labelCls}>What areas do you service?</label>
        <input value={form.service_areas} onChange={set("service_areas")} placeholder="e.g. Dallas, Fort Worth, Plano..." data-testid="tech-areas" className={inputCls} /></div>
      <div><label className={labelCls}>Briefly describe your professional experience</label>
        <textarea rows="4" value={form.experience} onChange={set("experience")} data-testid="tech-experience" className={inputCls}></textarea></div>

      <div className="grid sm:grid-cols-2 gap-4">
        <FileInput label="Resume (optional)" file={files.resume} onChange={(f) => setFiles((x) => ({ ...x, resume: f }))} testid="tech-resume" />
        <FileInput label="Certifications (optional)" file={files.certifications_file} onChange={(f) => setFiles((x) => ({ ...x, certifications_file: f }))} testid="tech-certs-file" />
      </div>

      <SubmitButton submitting={submitting} label="Submit Application" testid="tech-submit" />
    </form>
  );
}
